import React, { MouseEventHandler, useRef } from 'react';
import ReactSelect, { Props as SelectProps } from 'react-select';
import AsyncSelect from 'react-select/async';
import Creatable from 'react-select/creatable';
import styled, { css } from 'styled-components';
import { Flex } from 'reflexbox';

import { FieldContainer } from 'packages/kakapo-ui-kit/components/forms/controls/Field';
import { toRgbString } from 'packages/kakapo-ui-kit/helpers/HexColor';
import Column from 'packages/kakapo-ui-kit/components/grid/Column';
import { useTranslation } from 'react-i18next';

export interface ISelectOptionElementProps {
	isFocused: boolean;
	isSelected: boolean;
}

export interface ISelectOptionInnerProps {
	id: string;
	key: string;
	onClick: MouseEventHandler;
	onMouseOver: MouseEventHandler;
	tabIndex: number;
}

interface ISelectorOptionProps extends ISelectOptionElementProps {
	label: string;
	color?: string;
	innerProps: ISelectOptionInnerProps;
}

const DefaultSelectorOptionElement = styled.div<ISelectOptionElementProps>`
	cursor: pointer;
	color: rgba(${(props) => toRgbString(props.theme.colors.antracite)}, 0.6);
	font-size: 16px;
	padding: 0 14px;
	border-top: 1px solid ${(props) => props.theme.colors.lightBlue};

	&:hover {
		background-color: transparent;
		color: rgba(${(props) => toRgbString(props.theme.colors.antracite)}, 1);
	}

	${(props) =>
		props.isFocused &&
		`
		background-color: transparent;
		color: rgba(${toRgbString(props.theme.colors.antracite)}, 1.0);
	`}

	&:active {
		border-top-color: ${(props) => props.theme.colors.lightBlue};
		background-color: ${(props) => props.theme.colors.lightBlue};

		& + .react-select__option {
			border-top: 1px solid ${(props) => props.theme.colors.lightBlue};
		}
	}
`;

const DefaultSelectOption = ({
	label,
	isFocused,
	isSelected,
	innerProps,
}: ISelectorOptionProps) => {
	return (
		<DefaultSelectorOptionElement
			isFocused={isFocused}
			isSelected={isSelected}
			// eslint-disable-next-line react/jsx-props-no-spreading
			{...innerProps}
		>
			<Flex flexDirection="row" alignItems="center">
				<Column my={3} ml={2} mr={3}>
					{/* TODO: Add color before label */}
					{label}
				</Column>
			</Flex>
		</DefaultSelectorOptionElement>
	);
};

const style = css<SelectProps & { small?: boolean }>`
	.react-select__dropdown-indicator {
		svg path {
			fill: ${(props) => props.theme.colors.lightBlueDark};
		}
	}

	.react-select__control {
		background-color: ${(props) => props.theme.colors.whiteBlue};
		border: 1px solid ${(props) => props.theme.colors.lightBlueDark};
		color: ${(props) => props.theme.colors.slateBlue};
		border-radius: 4px;
		padding: 4px 4px 4px 16px;
		text-align: left;
		font-size: 16px;
		font-family: inherit;
		line-height: 1;
		cursor: text;
		transition: box-shadow 70ms ease-out, background-color 70ms ease-out, border-color 70ms ease-out;

		&.react-select__control--is-disabled {
			background-color: white;
			border-color: rgba(0, 0, 0, 0.1);

			.react-select__placeholder {
				color: rgba(255, 255, 255, 0.2);
			}
		}

		&:hover {
			border-color: ${(props) => props.theme.colors.lightBlueDark};
		}

		&--is-focused {
			background-color: ${(props) => props.theme.colors.white};
			border-color: rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.6);
			box-shadow: 0 1px 6px 0 rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.6);

			&:hover {
				border-color: rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.6);
			}
		}

		&--menu-is-open {
			border-radius: 4px 4px 0 0;
			border-width: 1px 1px 0 1px;
			box-shadow: none;

			.react-select__dropdown-indicator svg path {
				fill: ${(props) => props.theme.colors.primary};
			}
		}
	}

	.react-select__indicator-separator {
		background-color: ${(props) => props.theme.colors.lightBlueDark};
	}

	.react-select__menu {
		margin-top: 0;
		border-radius: 0 0 4px 4px;
		border: 0;
		outline: 0;
		padding: 0;
		border: 1px solid rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.6);
		border-width: 0 1px 1px 1px;
		box-shadow: none;
		overflow: hidden;
		position: absolute;
		z-index: 20;
	}

	.react-select__menu-list {
		padding: 0;
	}

	.react-select__value-container {
		/* Reset padding on input field, all padding is placed using the control */
		padding: 0;
	}

	.react-select__multi-value {
		border-radius: 15px;
		padding: 4px 6px;
		background-color: ${(props) => props.theme.colors.slateBlue};
	}

	.react-select__multi-value__label {
		color: ${(props) => props.theme.colors.white};
	}

	.react-select__multi-value__remove {
		border-radius: 10px;

		svg path {
			fill: ${(props) => props.theme.colors.white};
		}

		:hover {
			background-color: ${(props) => props.theme.colors.white};

			svg path {
				fill: ${(props) => props.theme.colors.slateBlue};
			}
		}
	}

	${(props) =>
		props.small &&
		`
		margin-bottom: 0;
	`}
`;

const SelectElement = styled(ReactSelect)`
	${style}
`;

const AsyncSelectElement = styled(AsyncSelect)`
	${style}
`;

const CreatableElement = styled(Creatable)`
	${style}
`;

const SmallSelectElement = styled(SelectElement)`
	.react-select__control {
		background: white;
		line-height: 12px;
		padding: 0px;
		padding-left: 7px;
		font-size: 14px;
		font-weight: normal;
	}
`;

interface IProps {
	creatable?: boolean;
	isAsync?: boolean;
	isMulti?: boolean;
	multi?: boolean;
	unspaced?: boolean;
	small?: boolean;
}

export default function Select<T, IsMulti extends boolean = false>({
	creatable,
	isAsync,
	multi,
	unspaced,
	small,
	...restProps
}: SelectProps<T, IsMulti> & IProps) {
	const ref = useRef<ReactSelect>(null);

	const element = () => {
		if (creatable) {
			return CreatableElement;
		}

		if (small) {
			return SmallSelectElement;
		}

		if (isAsync) {
			return AsyncSelectElement;
		}

		return SelectElement;
	};

	const { isDisabled, ...elementProps } = restProps;

	const { t } = useTranslation();

	return (
		<FieldContainer unspaced={unspaced}>
			{React.createElement(element(), {
				classNamePrefix: 'react-select',
				components: {
					Option: DefaultSelectOption,
				} as any,
				placeholder: t('common.select.placeholder'),
				ref: ref as any,
				...elementProps,
				isDisabled,
				isMulti: multi,
			} as any)}
		</FieldContainer>
	);
}
